// One AudioContext for the whole app.
//
// Browsers only allow a few contexts per page (~6 in Chrome) and each one holds
// an audio device handle, so anything that needs Web Audio shares this instead
// of opening its own. Created lazily on first use: a context opened before the
// user has interacted starts `suspended` on most browsers, and decoding does not
// need it running anyway — only playback would.
//
// `decodeAudioData` resamples to the context's rate, which is the device rate.
// That is exactly what playback wants, and it leaves durations untouched: a
// resample changes the frame count, never the length in seconds.

const AudioContextClass =
  typeof window !== 'undefined' &&
  (window.AudioContext || window.webkitAudioContext);

let sharedContext = null;

export const isAudioContextSupported = () => Boolean(AudioContextClass);

/**
 * The shared AudioContext, or null when Web Audio is unavailable (callers then
 * fall back to their plain `<audio>` path rather than failing).
 */
export const getSharedAudioContext = () => {
  if (!AudioContextClass) return null;
  if (!sharedContext || sharedContext.state === 'closed') {
    try {
      sharedContext = new AudioContextClass({ latencyHint: 'interactive' });
    } catch (error) {
      sharedContext = null;
    }
  }
  return sharedContext;
};

/** Close and forget the shared context. Used on teardown and by tests. */
export const closeSharedAudioContext = () => {
  if (sharedContext && sharedContext.state !== 'closed') {
    sharedContext.close().catch(() => {});
  }
  sharedContext = null;
};

export default getSharedAudioContext;
